"use client"

import { useRef, useState, type ReactNode } from "react"
import { Trash2 } from "lucide-react"

// Swipe a row left to delete it.
// ---------------------------------------------------------------------------
// Notifications had no way to be removed anywhere except the full
// /dashboard/client/notifications page. In the sheet — which is where people
// actually read them — the gesture everyone tries does nothing, so the list
// only ever grows.
//
// WHY POINTER EVENTS AND NOT TOUCH EVENTS: the sheet is dragged vertically by
// its own grip handler, and the list scrolls. Pointer events give us
// setPointerCapture, so once a row decides a gesture is horizontal it keeps
// receiving the moves even as the finger wanders vertically — without that,
// a slightly diagonal swipe gets stolen mid-way by the scroller and the row
// snaps back under your thumb.
//
// The axis is decided ONCE, on the first few pixels of movement, and never
// revisited for that gesture:
//   · mostly horizontal → this row owns it, and the page must not scroll
//   · mostly vertical   → the list owns it, and this row stays put
// Deciding per-frame is what makes home-grown swipe rows feel like they are
// fighting the scroller.
//
// Left only. A right swipe is the iOS back gesture, which the native shell
// enables (SceneDelegate: allowsBackForwardNavigationGestures), and stealing
// it here would break navigating back out of a screen.

/** Movement before the axis is locked. Below this, nothing is claimed. */
const AXIS_LOCK_PX = 8
/** How far the row travels before release deletes it. */
const COMMIT_PX = 96
/** The red zone stops growing here, so a long drag doesn't look broken. */
const MAX_PX = 128

export function SwipeToDelete({
  children,
  onDelete,
  disabled,
  label = "Delete",
}: {
  children: ReactNode
  onDelete: () => void
  disabled?: boolean
  label?: string
}) {
  const [dx, setDx] = useState(0)
  const [removing, setRemoving] = useState(false)
  const start = useRef<{ x: number; y: number } | null>(null)
  const axis = useRef<"undecided" | "horizontal" | "vertical">("undecided")

  function onPointerDown(e: React.PointerEvent) {
    if (disabled || removing) return
    // Mouse drags are not a gesture anyone expects on a desktop list; the
    // full notifications page has a visible Trash button for that.
    if (e.pointerType === "mouse") return
    start.current = { x: e.clientX, y: e.clientY }
    axis.current = "undecided"
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!start.current || removing) return
    const deltaX = e.clientX - start.current.x
    const deltaY = e.clientY - start.current.y

    if (axis.current === "undecided") {
      if (Math.abs(deltaX) < AXIS_LOCK_PX && Math.abs(deltaY) < AXIS_LOCK_PX) return
      axis.current = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical"
      if (axis.current === "horizontal") {
        // Keep the moves coming even if the finger drifts off the row.
        e.currentTarget.setPointerCapture?.(e.pointerId)
      }
    }
    if (axis.current !== "horizontal") return

    // Left only, and it slows as it goes so the limit is felt rather than hit.
    const travel = Math.min(0, deltaX)
    setDx(Math.max(-MAX_PX, travel < -COMMIT_PX ? -COMMIT_PX + (travel + COMMIT_PX) / 3 : travel))
  }

  function settle() {
    if (!start.current) return
    const committed = dx <= -COMMIT_PX
    start.current = null
    axis.current = "undecided"
    if (committed) {
      // Run the row out before unmounting it, so the list closes the gap on
      // something that has already left rather than on a row mid-flight.
      setRemoving(true)
      setDx(-MAX_PX)
      window.setTimeout(onDelete, 180)
      return
    }
    setDx(0)
  }

  const open = dx < 0

  return (
    <div className="relative overflow-hidden">
      {/* The zone under the row. Only rendered while the row has moved, so a
          resting list has no hidden red edge waiting to peek through. */}
      {open && (
        <div className="absolute inset-y-0 right-0 flex items-center justify-end bg-red-500 pr-5" style={{ width: MAX_PX }}>
          <Trash2 className="h-5 w-5 text-white" aria-hidden />
          <span className="sr-only">{label}</span>
        </div>
      )}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={settle}
        onPointerCancel={settle}
        className="relative bg-background"
        style={{
          transform: `translate3d(${dx}px,0,0)`,
          // No transition while the finger is down: the row must track it
          // exactly, or the gesture feels like it is lagging behind.
          transition: start.current ? "none" : "transform 180ms cubic-bezier(0.32,0.72,0,1)",
          // Let the list scroll vertically; we only ever claim the x axis.
          touchAction: "pan-y",
        }}
      >
        {children}
      </div>
    </div>
  )
}
